import { defineStore } from 'pinia'

/* Utils */
import { filterPointsByTags } from '@/store/delivery/delivery.utils'

/* Constants */
import { STATE_OF_MAP } from '@/constants/stateOfMap'

/* Types */
import { getDistanceBetweenPoints } from '@/store/delivery/delivery.utils'
import { StoreId } from '@/store'
import {
  AttentionFactoid,
  DeliveryState,
} from '@/store/delivery/delivery.types'
import {
  DeliveryPage,
  LatLng,
  AreasData,
  AreaProperties,
  PointProperties,
  PointFilter,
  Tag,
  DragCoordinates,
} from '@/api/modules/delivery/types'
import { Seo } from '@/types/main'
import { PlacesSearch } from '@/store/delivery/delivery.types'
import { StateGeoAddress, StateOfMap } from '@/types/mapServices'
import { Success } from '@/api/results'

export const useDeliveryStore = defineStore({
  id: StoreId.DELIVERY,
  state: (): DeliveryState => ({
    areas: [],
    points: [],
    page: {
      seo: {
        description: undefined,
        title: undefined,
        keywords: undefined,
      },
      title: null,
      pointFilters: [],
      breadcrumbs: [],
      noticeText: null,
      additives: [],
      attentionFactoid: {
        text: null,
        title: null,
      },
    },
    activeTags: [],
    coordinates: [0, 0],
    deliveryAddress: {
      address: {
        place: null,
        address: null,
      },
      state: StateGeoAddress.READY,
      coordinates: [0, 0],
    },
    stateLoadMap: STATE_OF_MAP.loading,
    isLoadingAreas: false,
  }),
  actions: {
    async updateAreas(coords: DragCoordinates) {
      this.isLoadingAreas = true

      await this.setAreasByCoords(coords)

      this.isLoadingAreas = false
    },

    async setAreasByCoords(coords: DragCoordinates) {
      const { $apiService } = useNuxtApp()

      const result = await $apiService.delivery.fetchAreasByCoordinates(coords)

      if (!(result instanceof Success)) {
        return
      }

      this.setAreasData(result.data)
    },

    setAreasData(areasData: AreasData | null) {
      if (!areasData) return

      this.setPoints(areasData.points)
      this.setAreas(areasData.areas)
    },

    setPoints(points: PointProperties[]) {
      this.points = points
    },

    setAreas(areas: AreaProperties[]) {
      this.areas = areas
    },

    setActiveTags(tags: string[]) {
      this.activeTags = tags
    },

    setDeliverySearch(
      place: PlacesSearch,
      address?: SuggestedOfAddress | null,
    ) {
      Object.assign(this.deliveryAddress.address, { place, address })
    },

    setStateLoadMap(status: StateOfMap) {
      this.stateLoadMap = status
    },

    setDeliveryCoordinates(coords: LatLng) {
      this.deliveryAddress.coordinates = coords
    },

    setStateAddress(state: StateGeoAddress) {
      this.deliveryAddress.state = state
    },

    setPage(page: DeliveryPage | null) {
      const attentionFactoid: AttentionFactoid = {
        text: page?.factoid_text || '',
        title: page?.factoid_title || '',
      }

      this.page.attentionFactoid = attentionFactoid

      const seo: Seo = {
        title: page?.title,
        keywords: page?.seo.seo_keywords,
        description: page?.seo.seo_description,
      }

      this.page.seo = seo

      const defaultEnabledTags = page?.point_filters
        .filter((tag: PointFilter) => tag.enabled)
        .map((tag: PointFilter) => tag.slug)

      this.activeTags = defaultEnabledTags || this.activeTags

      this.page.additives = page?.additives || this.page?.additives

      this.page.breadcrumbs = page?.breadcrumbs || this.page.breadcrumbs

      this.page.noticeText = page?.notice_text || this.page.noticeText

      this.page.pointFilters = page?.point_filters || this.page.pointFilters

      this.page.title = page?.page_title || this.page.title

      this.coordinates = page?.coordinates || this.coordinates
    },

    setPointsDistance(coordinates: number[]) {
      this.filteredPoints.forEach((point) => {
        const distance = getDistanceBetweenPoints(
          point.coordinates,
          coordinates,
        )

        this.setDistanceForPointById({
          distance,
          id: point.id,
        })
      })
    },

    setDistanceForPointById({
      id,
      distance,
    }: {
      id: string | number | undefined
      distance: number
    }) {
      if (!this.points) {
        return
      }

      const index = this.points.findIndex((feature) => feature.id === id)

      this.points[index].distance = distance
    },
  },
  getters: {
    pickupPoints: ({ points }) => filterPointsByTags(points, ['pickup']),

    cafePoints: ({ points }) => filterPointsByTags(points, ['cafe']),

    filteredPoints: ({ points, activeTags }) =>
      filterPointsByTags(points, activeTags),

    filteredTags: ({ page, points, activeTags }) => {
      if (!page) return []

      const filteredPoints = filterPointsByTags(points, activeTags) || []

      const tagsFromPoints: Tag[] = filteredPoints.flatMap((item) => item.tags)

      const uniqTagsFromPoints: string[] = []

      /* Собираем названия тэгов */
      for (const tag of tagsFromPoints) {
        /* Добавляем только уникальные тэги */
        if (!uniqTagsFromPoints.includes(tag.slug)) {
          uniqTagsFromPoints.push(tag.slug)
        }
      }

      const tagsFromDelivery = page.pointFilters.filter((f) => f.show)

      const filteredTagsList: typeof tagsFromDelivery = []

      for (const tagFromDelivery of tagsFromDelivery) {
        if (uniqTagsFromPoints.includes(tagFromDelivery.slug)) {
          filteredTagsList.push(tagFromDelivery)
        }
      }

      return filteredTagsList
    },

    getPointBySlug:
      ({ points }) =>
      (pointSlug: string): PointProperties | null => {
        if (!points) return null

        return points.find(({ slug }) => slug === pointSlug) || null
      },

    attentionFactoid: ({ page }) => {
      if (page.attentionFactoid.text || page.attentionFactoid.title)
        return page.attentionFactoid

      return {
        title: '',
        text: '',
      }
    },
  },
})
